import { BrandSummary, PerformanceStatus, RegionalData } from '@/types/pricing-regional.types';
import { RegionalStationsData } from '@/types/regional-stations.types';
import { FuelType } from '@/types/price-comparison.types';
import { ApiResponse } from '@/types/services';

import { apiService, downloadBlob } from './api';
import { baseApi } from './base-api';

export interface PricingFilters {
  region?: string;
  includeDiscounts?: boolean;
  [key: string]: any;
}

export interface StationOptions {
  region: string;
  sortBy?: string;
  brandFilter?: string;
}

export type BrandItem = {
  id: number;
  name: string;
};

export type GetBrandFiltersResponse = ApiResponse<{
  brands: BrandItem[];
  fuelTypes: string[];
  regions: string[];
}>;

export type GetSummaryResponse = ApiResponse<{
  data: BrandSummary[];
  fuel_types?: FuelType[];
  performanceStatus?: PerformanceStatus;
}>;

export type GetRegionalResponse = ApiResponse<{
  data: RegionalData[];
  fuel_types?: FuelType[];
}>;

export type GetStationsResponse = ApiResponse<{
  data: RegionalStationsData;
  brands: BrandItem[];
  fuel_types?: FuelType[];
  performanceStatus?: PerformanceStatus;
}>;

export type SummaryResult = {
  summaryData: BrandSummary[];
  summaryFuelTypes?: FuelType[];
  performanceStatus?: PerformanceStatus;
};

export type RegionalResult = {
  regionalData: RegionalData[];
  regionalFuelTypes?: FuelType[];
};

export type RegionStationsResult = {
  stationsData: RegionalStationsData;
  brands: BrandItem[];
  stationsFuelTypes?: FuelType[];
  performanceStatus?: PerformanceStatus;
};

export const pricingRegionalApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    getBrandFilters: builder.query<GetBrandFiltersResponse['data'], void>({
      query: () => '/pricing-regional/filters',
      providesTags: ['BrandOrder'],
      transformResponse: (response: GetBrandFiltersResponse) => {
        return response.data || {};
      }
    }),
    getSummary: builder.query<SummaryResult, PricingFilters | void>({
      query: filters => {
        return {
          url: '/pricing-regional/summary',
          params: filters ?? {}
        };
      },
      providesTags: ['Summary'],
      transformResponse: (response: GetSummaryResponse): SummaryResult => {
        return {
          summaryData: response.data.data || [],
          summaryFuelTypes: response.data.fuel_types || [],
          performanceStatus: response.data.performanceStatus
        };
      }
    }),
    getRegionalData: builder.query<RegionalResult, void>({
      query: () => '/pricing-regional/regional?includeDiscounts=true',
      transformResponse: (response: GetRegionalResponse): RegionalResult => {
        return {
          regionalData: response.data.data || [],
          regionalFuelTypes: response.data.fuel_types || []
        };
      },
      providesTags: ['RegionalSummary']
    }),
    getRegionStationsData: builder.query<RegionStationsResult, StationOptions>({
      query: ({ region, brandFilter, ...options }) => ({
        url: `/pricing-regional/region/${region}`,
        params: {
          ...options,
          ...(brandFilter ? { brandFilter } : {})
        }
      }),
      transformResponse: (response: GetStationsResponse): RegionStationsResult => {
        return {
          stationsData: response.data.data,
          stationsFuelTypes: response.data.fuel_types || [],
          brands: response.data.brands || [],
          performanceStatus: response.data.performanceStatus
        };
      }
    }),

    exportDetailedToExcel: builder.mutation<boolean, boolean | undefined>({
      async queryFn(includeDiscounts = true) {
        try {
          const { blob, filename } = await apiService.getBlob(
            `/pricing-regional/export/summary?includeDiscounts=${includeDiscounts}`
          );

          downloadBlob(blob, filename);

          return { data: true };
        } catch (error) {
          return { error: error as any };
        }
      }
    }),

    exportRegionalToExcel: builder.mutation<boolean, { region: string; includeDiscounts: boolean }>({
      async queryFn({ region, includeDiscounts }) {
        try {
          const { blob, filename } = await apiService.getBlob(
            `/pricing-regional/export/regional/${region}?includeDiscounts=${includeDiscounts}`
          );

          downloadBlob(blob, filename);

          return { data: true };
        } catch (error) {
          return { error: error as any };
        }
      }
    })
  })
});
export const {
  useGetBrandFiltersQuery,
  useGetSummaryQuery,
  useGetRegionalDataQuery,
  useGetRegionStationsDataQuery,
  useExportDetailedToExcelMutation,
  useExportRegionalToExcelMutation
} = pricingRegionalApi;
